import { InvalidParamError } from '../../../errors/InvalidParamError';

export type JwtPayload = {
  [key: string]: unknown;
  exp: number;
  iat: number;
  scope: string;
  singleUse?: boolean;
  sub: string;
};

/**
 * Decodes a JWT token and returns the full payload.
 *
 * This function extracts and returns the complete JWT payload including scopes,
 * expiration time, and other claims.
 *
 * @param jwt - The JWT token string.
 * @returns The decoded JWT payload object.
 * @throws InvalidParamError if the token is invalid or cannot be decoded.
 *
 * @example
 * ```typescript
 * const payload = decodeJwt(jwt);
 * // Returns: { scopes: ['wallet:export'], exp: 1234567890, ... }
 * ```
  * @notInstrumented
 */
// eslint-disable-next-line custom-rules/require-single-object-param
export const decodeJwt = (jwt: string): JwtPayload => {
  try {
    // JWT format: header.payload.signature
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      throw new InvalidParamError('Invalid JWT format');
    }

    // Decode the payload (second part)
    // Base64URL decode: replace URL-safe characters and decode
    const base64Payload = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    // Add padding if needed
    const paddedPayload =
      base64Payload + '='.repeat((4 - (base64Payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);
    const claims = JSON.parse(decodedPayload) as JwtPayload;

    return claims;
  } catch (error) {
    if (error instanceof InvalidParamError) {
      throw error;
    }
    throw new InvalidParamError(
      `Failed to decode JWT: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};
